import AutorenewIcon from '@mui/icons-material/Autorenew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GridViewIcon from '@mui/icons-material/GridView';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import debounce from 'lodash.debounce';
import React, { useEffect, useMemo, useState } from 'react';
import CustomPagination from '../CustomPagination';

const SEARCH_DEBOUNCE_MS = 400;

/** Sentinel id for the pinned "packages that matched no address" tile. */
export const UNMATCHED_ID = 'unmatched';

const SORT_OPTIONS = [
  { value: 'count', label: 'Package count' },
  { value: 'nickname', label: 'Name' },
  { value: 'address1', label: 'Street address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' }
];

/**
 * Packages by client address.
 *
 * Fully controlled: search, sort and pagination all happen on the server, and
 * every selection lives on the page. One tile per `addresses` row, plus a
 * pinned "Unmatched" tile for packages that matched none of them.
 */
export default function PackagesByAddressSummary({
  rows = [],
  summary = {},
  pagination = {},
  loading = false,
  search = '',
  sortBy = 'count',
  sortOrder = 'DESC',
  selectedIds = [],
  selectedLabels = {},
  includeUnmatched = false,
  onSearchChange,
  onSortChange,
  onPageChange,
  onToggleAddress,
  onToggleUnmatched,
  onRemoveSelection,
  onClearSelections,
  onRefresh,
  canRecount = false,
  recounting = false,
  onRecountAll
}) {
  const [layout, setLayout] = useState('cards');
  // Local mirror so the input stays responsive while the request is debounced.
  const [searchDraft, setSearchDraft] = useState(search);
  // Ephemeral UI feedback for the recount action — not business state, so it
  // stays local rather than living on the page like the rest of this panel's
  // data.
  const [recountSnackbar, setRecountSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleRecountAll = async (e) => {
    e.stopPropagation();
    try {
      const stats = await onRecountAll();
      setRecountSnackbar({
        open: true,
        message: `Recount complete: ${stats.matched} matched, ${stats.unmatched} unmatched (${stats.changed} changed) in ${(stats.elapsedMs / 1000).toFixed(1)}s.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to recount address matches:', error);
      setRecountSnackbar({
        open: true,
        message: 'Recount failed. Please try again.',
        severity: 'error'
      });
    }
  };

  const debouncedSearch = useMemo(
    () => debounce((value) => onSearchChange(value), SEARCH_DEBOUNCE_MS),
    [onSearchChange]
  );

  // Without this the debounced call fires after unmount and warns.
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Keep the draft in step when the page resets the query (e.g. Reset All).
  useEffect(() => setSearchDraft(search), [search]);

  const {
    max_count: maxCount = 1,
    unmatched_count: unmatchedCount = 0,
    package_total: packageTotal = 0,
    address_total: addressTotal = 0
  } = summary;

  const { currentPage = 1, totalPages = 1, limit = 12 } = pagination;
  const total = pagination.total ?? 0;

  const selectionCount = selectedIds.length + (includeUnmatched ? 1 : 0);
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * limit + 1;
  const rangeEnd = Math.min(currentPage * limit, total);

  const handleSearchChange = (event) => {
    setSearchDraft(event.target.value);
    debouncedSearch(event.target.value);
  };

  const handleLayoutChange = (_, next) => {
    if (next) setLayout(next);
  };

  const handleColumnSort = (field) => {
    if (sortBy === field) {
      onSortChange(field, sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      onSortChange(field, field === 'count' ? 'DESC' : 'ASC');
    }
  };

  // Scale bars against the global maximum, not this page's, so a bar means the
  // same thing on page 1 and page 3.
  const barValue = (count) => Math.round((count / Math.max(1, maxCount)) * 100);

  const countColor = (count) => {
    const ratio = count / Math.max(1, maxCount);
    if (ratio >= 0.75) return 'error';
    if (ratio >= 0.4) return 'warning';
    return 'primary';
  };

  const secondaryLine = (row) =>
    [row.address1, row.address2, row.city, row.state, row.zip_code]
      .filter(Boolean)
      .join(', ');

  return (
    <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
        >
          <LocationOnIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Packages by Address
          </Typography>
          <Chip
            label={`${addressTotal} address${addressTotal !== 1 ? 'es' : ''}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          {packageTotal > 0 && (
            <Tooltip title="Every package is assigned to at most one address, so these always add up.">
              <Chip
                label={`${packageTotal - unmatchedCount} matched / ${unmatchedCount} unmatched`}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          )}

          <Tooltip title="Refresh counts">
            <span>
              <IconButton
                size="small"
                disabled={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Distinct from Refresh: this re-runs the matching algorithm against
              every package rather than just re-displaying current counts. */}
          {canRecount && (
            <Tooltip title="Recount all — re-run address matching for every package">
              <span>
                <IconButton
                  size="small"
                  disabled={recounting}
                  onClick={handleRecountAll}
                >
                  <AutorenewIcon
                    fontSize="small"
                    sx={
                      recounting
                        ? {
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                              '0%': { transform: 'rotate(0deg)' },
                              '100%': { transform: 'rotate(360deg)' }
                            }
                          }
                        : undefined
                    }
                  />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* One chip per selection, so a tile selected on another page stays visible */}
          {selectedIds.map((id) => (
            <Chip
              key={id}
              label={selectedLabels[id] ?? `#${id}`}
              size="small"
              color="success"
              onDelete={(e) => {
                e.stopPropagation();
                onRemoveSelection(id);
              }}
            />
          ))}
          {includeUnmatched && (
            <Chip
              label="Unmatched"
              size="small"
              color="warning"
              onDelete={(e) => {
                e.stopPropagation();
                onRemoveSelection(UNMATCHED_ID);
              }}
            />
          )}
          {selectionCount > 1 && (
            <Chip
              label="Clear all"
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                onClearSelections();
              }}
            />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 1, pb: 2, px: 2 }}>
        {/* Toolbar: search + sort + layout */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <TextField
            size="small"
            placeholder="Search name, street, city, state, zip..."
            value={searchDraft}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 260 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* The card view has no header row to click, so it needs a control */}
            {layout === 'cards' && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="address-sort-label">Sort by</InputLabel>
                <Select
                  labelId="address-sort-label"
                  label="Sort by"
                  value={sortBy}
                  onChange={(e) => onSortChange(e.target.value, sortOrder)}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {layout === 'cards' && (
              <ToggleButtonGroup
                value={sortOrder}
                exclusive
                size="small"
                onChange={(_, next) => next && onSortChange(sortBy, next)}
              >
                <ToggleButton value="DESC">Desc</ToggleButton>
                <ToggleButton value="ASC">Asc</ToggleButton>
              </ToggleButtonGroup>
            )}

            <ToggleButtonGroup
              value={layout}
              exclusive
              onChange={handleLayoutChange}
              size="small"
              aria-label="layout"
            >
              <ToggleButton value="cards" aria-label="card grid view">
                <Tooltip title="Card view">
                  <GridViewIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="table" aria-label="table list view">
                <Tooltip title="List view">
                  <ViewListIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Pinned Unmatched tile — outside search and pagination, always shown
            so the totals visibly reconcile and the layout never shifts. */}
        <Card
          variant="outlined"
          sx={{
            mb: 2,
            borderWidth: includeUnmatched ? 2 : 1,
            borderColor: includeUnmatched ? 'warning.main' : 'divider',
            bgcolor: includeUnmatched ? 'warning.50' : 'background.paper',
            opacity: unmatchedCount === 0 ? 0.6 : 1
          }}
        >
          <CardActionArea
            disabled={unmatchedCount === 0}
            onClick={onToggleUnmatched}
          >
            <CardContent
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.5,
                pb: '12px !important'
              }}
            >
              <LocationOffIcon
                fontSize="small"
                color={includeUnmatched ? 'warning' : 'disabled'}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: includeUnmatched ? 600 : 500 }}
                >
                  Unmatched
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Scanned address did not match any address on file
                </Typography>
              </Box>
              <Chip
                label={`${unmatchedCount} pkg${unmatchedCount !== 1 ? 's' : ''}`}
                size="small"
                color={includeUnmatched ? 'warning' : 'default'}
                variant={includeUnmatched ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
            </CardContent>
          </CardActionArea>
        </Card>

        {loading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <LinearProgress sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Loading address summary...
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ py: 2 }} color="text.secondary" variant="body2">
            {search
              ? `No addresses match "${search}".`
              : 'No addresses on file yet. Client addresses arrive from the daily vendor sync.'}
          </Typography>
        ) : layout === 'cards' ? (
          <Grid container spacing={2}>
            {rows.map((row) => {
              const isSelected = selectedIds.includes(row.address_id);
              const color = countColor(row.count);

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={row.address_id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderWidth: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'primary.50' : 'background.paper',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: 'primary.light',
                        boxShadow: 2
                      }
                    }}
                  >
                    <CardActionArea
                      onClick={() => onToggleAddress(row)}
                      sx={{ p: 0, height: '100%' }}
                    >
                      <CardContent sx={{ pb: '12px !important' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 1
                          }}
                        >
                          <LocationOnIcon
                            fontSize="small"
                            color={isSelected ? 'primary' : 'action'}
                            sx={{ mt: 0.3, mr: 0.5, flexShrink: 0 }}
                          />
                          <Chip
                            label={`${row.count} pkg${row.count !== 1 ? 's' : ''}`}
                            size="small"
                            color={isSelected ? 'primary' : color}
                            variant={isSelected ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 600, flexShrink: 0 }}
                          />
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isSelected ? 600 : 500,
                            lineHeight: 1.4,
                            wordBreak: 'break-word'
                          }}
                        >
                          {row.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mb: 1.5,
                            lineHeight: 1.4,
                            wordBreak: 'break-word'
                          }}
                        >
                          {secondaryLine(row)}
                        </Typography>

                        <Tooltip
                          title={`${barValue(row.count)}% of highest-volume address`}
                          placement="bottom"
                        >
                          <LinearProgress
                            variant="determinate"
                            value={barValue(row.count)}
                            color={isSelected ? 'primary' : color}
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                        </Tooltip>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortBy === 'nickname'}
                      direction={
                        sortBy === 'nickname' ? sortOrder.toLowerCase() : 'asc'
                      }
                      onClick={() => handleColumnSort('nickname')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortBy === 'address1'}
                      direction={
                        sortBy === 'address1' ? sortOrder.toLowerCase() : 'asc'
                      }
                      onClick={() => handleColumnSort('address1')}
                    >
                      Address
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 160 }}>
                    <TableSortLabel
                      active={sortBy === 'count'}
                      direction={
                        sortBy === 'count' ? sortOrder.toLowerCase() : 'desc'
                      }
                      onClick={() => handleColumnSort('count')}
                    >
                      Packages
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 180 }}>
                    Volume
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const isSelected = selectedIds.includes(row.address_id);
                  const color = countColor(row.count);

                  return (
                    <TableRow
                      key={row.address_id}
                      hover
                      selected={isSelected}
                      onClick={() => onToggleAddress(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <LocationOnIcon
                            fontSize="small"
                            color={isSelected ? 'primary' : 'action'}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: isSelected ? 600 : 400 }}
                          >
                            {row.label}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {secondaryLine(row)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.count}
                          size="small"
                          color={isSelected ? 'primary' : color}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`${barValue(row.count)}% of max`}>
                          <LinearProgress
                            variant="determinate"
                            value={barValue(row.count)}
                            color={isSelected ? 'primary' : color}
                            sx={{ height: 6, borderRadius: 3, width: 120 }}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Server-side pagination. CustomPagination renders nothing at 1 page. */}
        {!loading && rows.length > 0 && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {`Showing ${rangeStart}–${rangeEnd} of ${total} address${total !== 1 ? 'es' : ''}`}
            </Typography>
            <CustomPagination
              count={totalPages}
              page={currentPage}
              onChange={(_, nextPage) => onPageChange(nextPage)}
            />
          </Box>
        )}
      </AccordionDetails>

      <Snackbar
        open={recountSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setRecountSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setRecountSnackbar((s) => ({ ...s, open: false }))}
          severity={recountSnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {recountSnackbar.message}
        </Alert>
      </Snackbar>
    </Accordion>
  );
}
